﻿import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Pill,
  Plus,
  Calendar,
  Shield,
  ShieldAlert,
  ShieldX,
  AlertTriangle,
  Volume2,
  QrCode,
  Trash2,
  X,
  Package,
  Clock,
  CheckCircle,
  Filter,
  ChevronRight,
  Activity,
  FileText
} from 'lucide-react';
import type { PrescriptionAnalysis, AnalysisResult } from '../types';
import { generateSafetyIssues, getRiskSummary } from '../services/openRouterService';
import '../styles/MedicineCabinet.css';

export const MedicineCabinet: React.FC = () => {
  const [prescriptions, setPrescriptions] = useState<PrescriptionAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPrescription, setSelectedPrescription] =
    useState<PrescriptionAnalysis | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'completed'>(
    'all'
  );
  const [justSaved, setJustSaved] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const oldKey = 'pathya_prescriptions';
    const newKey = 'medcheck_prescriptions';
    
    const oldData = localStorage.getItem(oldKey);
    const newData = localStorage.getItem(newKey);
    
    let existingPrescriptions: PrescriptionAnalysis[] = [];
    
    if (!newData && oldData) {
      localStorage.setItem(newKey, oldData);
      localStorage.removeItem(oldKey);
      existingPrescriptions = JSON.parse(oldData);
    } else if (newData) {
      existingPrescriptions = JSON.parse(newData);
    }

    // Convert date strings back to Date objects
    existingPrescriptions = existingPrescriptions.map(p => ({
      ...p,
      createdAt: typeof p.createdAt === 'string' ? new Date(p.createdAt) : p.createdAt
    }));

    const incomingAnalysis = location.state?.analysis as AnalysisResult | undefined;
    
    if (incomingAnalysis) {
      const newPrescription: PrescriptionAnalysis = {
        prescriptionId: `presc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        userId: 'local_user',
        imageUrl: '',
        ocrText: incomingAnalysis.ocrText,
        ocrConfidence: 0.85,
        safetyScore: incomingAnalysis.safetyScore,
        status: 'completed',
        createdAt: new Date(),
        medicines: incomingAnalysis.medicines,
        interactions: incomingAnalysis.interactions,
        issues: incomingAnalysis.issues,
        warnings: incomingAnalysis.warnings,
        modelUsage: incomingAnalysis.modelUsage,
        patientInfo: incomingAnalysis.patientInfo,
      };

      const updatedPrescriptions = [newPrescription, ...existingPrescriptions];
      setPrescriptions(updatedPrescriptions);
      localStorage.setItem('medcheck_prescriptions', JSON.stringify(updatedPrescriptions));
      
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 3000);
      
      window.history.replaceState({}, document.title);
    } else {
      setPrescriptions(existingPrescriptions);
    }
    
    setLoading(false);
  }, [location.state]);

  const deletePrescription = (prescriptionId: string) => {
    const updated = prescriptions.filter(
      (p) => p.prescriptionId !== prescriptionId
    );
    setPrescriptions(updated);
    localStorage.setItem('medcheck_prescriptions', JSON.stringify(updated));
    setSelectedPrescription(null);
  };

  const getSafetyColor = (score: number): string => {
    if (score >= 80) return 'var(--success)';
    if (score >= 60) return 'var(--warning)';
    return 'var(--danger)';
  };

  const getSafetyIcon = (score: number) => {
    if (score >= 80) return <Shield className="safety-icon safe" />;
    if (score >= 60) return <ShieldAlert className="safety-icon caution" />;
    return <ShieldX className="safety-icon danger" />;
  };

  const getSafetyLabel = (score: number): string => {
    if (score >= 80) return 'Safe';
    if (score >= 60) return 'Caution';
    return 'High Risk';
  };

  const getPrescriptionSummary = (prescription: PrescriptionAnalysis) =>
    getRiskSummary({
      medicines: prescription.medicines,
      interactions: prescription.interactions || [],
      issues:
        prescription.issues ||
        generateSafetyIssues(prescription.medicines, prescription.interactions || []),
      warnings: prescription.warnings,
      safetyScore: prescription.safetyScore,
      ocrText: prescription.ocrText,
      modelUsage: prescription.modelUsage,
      patientInfo: prescription.patientInfo,
    });

  const filteredPrescriptions = useMemo(() => {
    // eslint-disable-next-line react-hooks/purity
    const now = Date.now();
    const filtered = prescriptions.filter((p) => {
      if (filterStatus === 'all') return true;
      if (filterStatus === 'active') {
        const createdDate = new Date(p.createdAt).getTime();
        const duration = parseInt(p.medicines[0]?.duration || '7');
        const expiryDate = createdDate + duration * 24 * 60 * 60 * 1000;
        return expiryDate > now;
      }
      return p.status === 'completed';
    });
    return filtered;
  }, [prescriptions, filterStatus]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="medicine-cabinet">
      {/* Background Elements */}
      <div className="cabinet-bg">
        <div className="bg-gradient-1"></div>
        <div className="bg-gradient-2"></div>
        <div className="bg-grid"></div>
      </div>

      <div className="container">
        {/* Success Notification */}
        <AnimatePresence>
          {justSaved && (
            <motion.div
              className="success-notification"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <CheckCircle size={20} />
              <span>Prescription saved successfully!</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header */}
        <motion.div 
          className="cabinet-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="header-content">
            <div className="header-icon">
              <Pill size={32} />
            </div>
            <div className="header-text">
              <h1>Medicine Cabinet</h1>
              <p>Your personal medicine history and safety tracker</p>
            </div>
          </div>
          <motion.button
            className="btn btn-primary add-btn"
            onClick={() => navigate('/upload')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Plus size={20} />
            <span>Add Prescription</span>
          </motion.button>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div 
          className="filter-tabs"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="filter-icon">
            <Filter size={18} />
          </div>
          {[
            { key: 'all', label: 'All', icon: FileText },
            { key: 'active', label: 'Active', icon: Clock },
            { key: 'completed', label: 'Completed', icon: CheckCircle }
          ].map((tab) => (
            <motion.button
              key={tab.key}
              className={`tab ${filterStatus === tab.key ? 'active' : ''}`}
              onClick={() => setFilterStatus(tab.key as typeof filterStatus)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <tab.icon size={16} />
              <span>{tab.label}</span>
              {tab.key === 'all' && (
                <span className="count">{prescriptions.length}</span>
              )}
            </motion.button>
          ))}
        </motion.div>

        {/* Content */}
        {loading ? (
          <div className="loading-state">
            <motion.div 
              className="loading-spinner"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <Activity size={40} />
            </motion.div>
            <p>Loading your medicine cabinet...</p>
          </div>
        ) : filteredPrescriptions.length === 0 ? (
          <motion.div 
            className="empty-state"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="empty-icon">
              <Package size={64} />
            </div>
            <h2>No prescriptions yet</h2>
            <p>Upload your first prescription to get started with medicine safety tracking</p>
            <motion.button
              className="btn btn-primary"
              onClick={() => navigate('/upload')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Plus size={20} />
              <span>Upload Your First Prescription</span>
            </motion.button>
          </motion.div>
        ) : (
          <div className="cabinet-content">
            {/* Prescriptions Grid */}
            <motion.div 
              className="prescriptions-grid"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {filteredPrescriptions.map((prescription) => (
                <motion.div
                  key={prescription.prescriptionId}
                  className={`prescription-card ${
                    selectedPrescription?.prescriptionId === prescription.prescriptionId
                      ? 'selected'
                      : ''
                  }`}
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  whileHover={{ y: -4, boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}
                  onClick={() => setSelectedPrescription(prescription)}
                >
                  <div className="card-header">
                    <div className="date-badge">
                      <Calendar size={14} />
                      <span>{new Date(prescription.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div
                      className={`safety-badge ${
                        prescription.safetyScore >= 80 ? 'safe' :
                        prescription.safetyScore >= 60 ? 'caution' : 'danger'
                      }`}
                    >
                      {getSafetyIcon(prescription.safetyScore)}
                      <span>{getSafetyLabel(prescription.safetyScore)}</span>
                    </div>
                  </div>

                  <div className="card-body">
                    <div className="medicine-count">
                      <Pill size={20} />
                      <span>
                        {prescription.medicines.length} Medicine
                        {prescription.medicines.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    
                    <div className="score-preview">
                      <div 
                        className="score-bar"
                        style={{ 
                          background: `linear-gradient(90deg, ${getSafetyColor(prescription.safetyScore)} ${prescription.safetyScore}%, var(--gray-200) ${prescription.safetyScore}%)`
                        }}
                      />
                      <span className="score-text">{Math.round(prescription.safetyScore)}/100</span>
                    </div>

                  {prescription.interactions && prescription.interactions.length > 0 && (
                      <div className="interaction-preview">
                        <AlertTriangle size={14} />
                        <span>{prescription.interactions.length} interaction{prescription.interactions.length !== 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </div>

                  <div className="card-footer">
                    <span>View Details</span>
                    <ChevronRight size={16} />
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Details Panel */}
            <AnimatePresence>
              {selectedPrescription && (
                <motion.div 
                  className="details-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setSelectedPrescription(null)}
                >
                  <motion.div 
                    className="details-panel"
                    initial={{ x: '100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '100%' }}
                    transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="panel-header">
                      <h2>Prescription Details</h2>
                      <motion.button
                        className="close-btn"
                        onClick={() => setSelectedPrescription(null)}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <X size={20} />
                      </motion.button>
                    </div>

                    <div className="panel-content">
                      {/* Safety Score Section */}
                      <div className="detail-section safety-section">
                        <h3>
                          <Shield size={18} />
                          Safety Assessment
                        </h3>
                        <div className="safety-display">
                          <div 
                            className="score-circle"
                            style={{ '--score-color': getSafetyColor(selectedPrescription.safetyScore) } as React.CSSProperties}
                          >
                            <svg viewBox="0 0 100 100">
                              <circle className="bg" cx="50" cy="50" r="45" />
                              <circle 
                                className="progress" 
                                cx="50" 
                                cy="50" 
                                r="45"
                                strokeDasharray={`${selectedPrescription.safetyScore * 2.83} 283`}
                              />
                            </svg>
                            <div className="score-value">
                              <span className="number">{Math.round(selectedPrescription.safetyScore)}</span>
                              <span className="label">/100</span>
                            </div>
                          </div>
                          <div className="safety-info">
                            <div className={`status-badge ${getPrescriptionSummary(selectedPrescription).level}`}>
                              {getSafetyIcon(selectedPrescription.safetyScore)}
                              <span>{getPrescriptionSummary(selectedPrescription).title}</span>
                            </div>
                            <p>{getPrescriptionSummary(selectedPrescription).description}</p>
                          </div>
                        </div>
                      </div>

                      {/* Medicines Section */}
                      <div className="detail-section">
                        <h3>
                          <Pill size={18} />
                          Medicines ({selectedPrescription.medicines.length})
                        </h3>
                        <div className="medicines-list">
                          {selectedPrescription.medicines.map((med, idx) => (
                            <motion.div 
                              key={idx} 
                              className="medicine-item"
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: idx * 0.05 }}
                            >
                              <div className="med-icon">
                                <Pill size={16} />
                              </div>
                              <div className="med-info">
                                <div className="med-name">{med.name}</div>
                                <div className="med-details">
                                  <span>{med.dosage}</span>
                                  <span>{med.frequency}</span>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </div>

                      {/* Interactions Section */}
                    {selectedPrescription.interactions && selectedPrescription.interactions.length > 0 && (
                        <div className="detail-section warnings-section">
                          <h3>
                            <AlertTriangle size={18} />
                            Interactions ({selectedPrescription.interactions.length})
                          </h3>
                          <div className="interactions-list">
                            {selectedPrescription.interactions.map((int, idx) => (
                              <motion.div
                                key={idx}
                                className={`interaction-item severity-${int.severity}`}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.05 }}
                              >
                                <div className="interaction-header">
                                  <AlertTriangle size={16} />
                                  <strong>{int.drug1} + {int.drug2}</strong>
                                  <span className="severity-tag">{int.severity}</span>
                                </div>
                                <p>{int.description}</p>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="panel-actions">
                      <motion.button 
                        className="btn btn-secondary"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Volume2 size={18} />
                        <span>Listen</span>
                      </motion.button>
                      <motion.button 
                        className="btn btn-secondary"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <QrCode size={18} />
                        <span>QR Code</span>
                      </motion.button>
                      <motion.button
                        className="btn btn-danger"
                        onClick={() => deletePrescription(selectedPrescription.prescriptionId)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Trash2 size={18} />
                        <span>Delete</span>
                      </motion.button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

